import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { baseurImage } from "../../../Baseurl";
import { logout } from "../../../store/userSlice";
import { fetchBusinessSettings } from "../../../store/businessSlice";
import BranchSelectorModal from "../Login/BranchSelectorModal";
import { getAssistantEnabled, toggleAssistantPanel } from "../../Assistant/assistantStatus";
import "./Sidebar.css";

// Initials for the brand tile, e.g. "Pine Bridge Health" -> "PB"
function initialsOf(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "GP";
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

export default function Sidebar({ isOpen, onToggle }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const businessSettings = useSelector((state) => state.business.businessSettings);
  const businessLoading = useSelector((state) => state.business.loading);
  const allBranchSettings = useSelector((state) => state.business.allBranchSettings);
  const activeBranchSettings = useSelector((state) => state.business.activeBranchSettings);
  const activeBranchId = useSelector((state) => state.business.activeBranchId);
  const [showBranchSwitch, setShowBranchSwitch] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  // Only show the help icon when the assistant module is switched on.
  const [assistantOn, setAssistantOn] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  // Falls back to the initials tile when the logo URL fails to load.
  const [logoBroken, setLogoBroken] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Esc closes the profile menu.
  useEffect(() => {
    if (!profileOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setProfileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [profileOpen]);

  // Fetch business settings on component mount
  useEffect(() => {
    const getUserId = () => {
      try {
        const userMedical = localStorage.getItem("userMedical");
        if (userMedical) {
          const userData = JSON.parse(userMedical);
          return userData?.id || userData?.data?.id || null;
        }
        return null;
      } catch (error) {
        console.error("Error parsing userMedical from localStorage:", error);
        return null;
      }
    };

    const doctorId = getUserId();

    if (doctorId && !businessSettings) {
      dispatch(fetchBusinessSettings(doctorId));
    }
  }, [dispatch]);


  useEffect(() => {
    if (user) {
      setAvatarVersion(Date.now());
    }
  }, [user]);


  // Shared with the assistant panel, so this costs no extra request.
  useEffect(() => {
    let alive = true;
    getAssistantEnabled().then((isOn) => { if (alive) setAssistantOn(isOn); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onProfileUpdated = () => setAvatarVersion(Date.now());
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  // A new logo URL deserves a fresh attempt.
  useEffect(() => {
    setLogoBroken(false);
  }, [businessSettings?.image, businessSettings?.logo, activeBranchId]);

  // Ticks once a minute — the clock only shows hours and minutes.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate("");
  };

  const getProfileImg = () => {
    // Fallbacks for first render after login
    let photo = user?.personalPhoto;
    if (!photo) {
      const lsPhoto = localStorage.getItem("userPersonalPhoto");
      if (lsPhoto) photo = lsPhoto;
      if (!photo) {
        try {
          const stored = JSON.parse(localStorage.getItem("userMedical") || "null");
          photo = stored?.data?.personalPhoto || null;
        } catch { }
      }
    }

    if (!photo) return "/assets/images/user/avatar-2.jpg";

    const bustKey = localStorage.getItem("profilePhotoBust") || avatarVersion;
    const bust = `?v=${bustKey}`;

    // Support Blob for immediate preview after upload
    if (typeof photo === "object" && photo instanceof Blob) {
      const url = URL.createObjectURL(photo);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return url;
    }

    if (typeof photo === "string" && (photo.startsWith("http://") || photo.startsWith("https://"))) {
      return `${photo}${bust}`;
    }

    return `${baseurImage}${String(photo).replace(/^\/+/, "")}${bust}`;
  };

  const getRoleLabel = () => {
    let role = user?.role;
    if (!role) {
      try {
        const stored = JSON.parse(localStorage.getItem("userMedical") || "null");
        role = stored?.role || stored?.data?.role || null;
      } catch { }
    }
    if (!role) return user?.email || "";
    return String(role).charAt(0).toUpperCase() + String(role).slice(1);
  };

  // Business logo, same sources the invoice/print views use:
  // the settings record (image/logo), the active branch, then the cache.
  const getBrandLogo = () => {
    let raw =
      businessSettings?.image ||
      businessSettings?.logo ||
      activeBranchSettings?.image ||
      activeBranchSettings?.logo ||
      null;

    if (!raw) raw = localStorage.getItem("businessSettingImage") || null;
    if (!raw) return null;

    raw = String(raw);
    if (raw.startsWith("data:") || raw.startsWith("http://") || raw.startsWith("https://")) {
      return raw;
    }
    return `${baseurImage}${raw.replace(/^\/+/, "")}`;
  };

  const officialName = businessSettings?.official_name?.trim() || "";
  const userName = user?.fullName || user?.name || "User";
  const isMultiBranch = allBranchSettings?.length > 1 && activeBranchId;
  const branchNumber = isMultiBranch
    ? allBranchSettings.findIndex((b) => b.id === activeBranchId) + 1
    : null;
  const brandLogo = logoBroken ? null : getBrandLogo();

  return (
    <header className="pc-header gp-topbar">
      <div className="header-wrapper">
        {/* ---------- left: menu toggle + brand ---------- */}
        <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={onToggle}
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            <i className={isOpen ? "ti ti-x" : "ti ti-menu-2"} />
          </button>

          <div className="gp-brand">
            <span
              className={`gp-brand-mark ${brandLogo && !businessLoading ? "gp-brand-mark-img" : ""}`}
              aria-hidden="true"
            >
              {businessLoading ? (
                <i className="ti ti-heartbeat" style={{ fontSize: 20 }} />
              ) : brandLogo ? (
                <img
                  src={brandLogo}
                  alt={officialName || "Logo"}
                  onError={() => setLogoBroken(true)}
                />
              ) : (
                initialsOf(officialName)
              )}
            </span>
            <span className="gp-brand-text">
              {businessLoading ? (
                <span className="gp-skeleton" />
              ) : (
                <span className="gp-brand-name" title={officialName}>{officialName}</span>
              )}
              <span className="gp-brand-sub">GreenPulse Medical System</span>
            </span>
          </div>

          {/* Branch pill — only shown when multi-branch */}
          {isMultiBranch && (
            <button
              type="button"
              className="gp-branch ms-3"
              onClick={() => setShowBranchSwitch(true)}
              title="Switch branch"
            >
              <span className="gp-dot" />
              <span className="gp-branch-label">
                Branch {branchNumber}
                {activeBranchSettings?.official_name ? ` — ${activeBranchSettings.official_name}` : ""}
              </span>
              <i className="ti ti-selector" style={{ fontSize: 13 }} />
            </button>
          )}

          {showBranchSwitch && allBranchSettings?.length > 1 && (
            <BranchSelectorModal
              branches={allBranchSettings}
              onSelect={() => setShowBranchSwitch(false)}
            />
          )}
        </div>

        {/* ---------- right: clock + actions + user ---------- */}
        <div className="gp-actions">
          <div className="gp-clock">
            <span className="gp-clock-time">
              {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
            <span className="gp-clock-date">
              {now.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })}
            </span>
          </div>

          <span className="gp-divider" />

          {/* Help assistant — opens the panel mounted in Admin.jsx. */}
          {assistantOn && (
            <button
              type="button"
              className="gp-icon-btn"
              onClick={toggleAssistantPanel}
              title="Help — how do I...?"
              aria-label="Help assistant"
            >
              <i className="ti ti-help-circle" />
            </button>
          )}

          {/* User chip — click opens the account menu. */}
          <div className="gp-user" ref={dropdownRef}>
            <button
              type="button"
              className={`gp-user-chip ${profileOpen ? "gp-open" : ""}`}
              onClick={() => setProfileOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={profileOpen}
            >
              <img src={getProfileImg()} alt="user" />
              <span className="gp-user-meta">
                <span className="gp-user-name">{userName}</span>
                <span className="gp-user-role">{getRoleLabel()}</span>
              </span>
              <i className="ti ti-chevron-down gp-caret" />
            </button>

            <div className={`gp-menu ${profileOpen ? "gp-open" : ""}`} role="menu">
              <div className="gp-menu-head">
                <img src={getProfileImg()} alt="user" />
                <span style={{ minWidth: 0 }}>
                  <span className="gp-mh-name d-block">{userName}</span>
                  <span className="gp-mh-mail">{user?.email}</span>
                </span>
              </div>

              <NavLink
                to="/Admin/myprofile"
                className="gp-menu-item"
                onClick={() => setProfileOpen(false)}
                role="menuitem"
              >
                <i className="ti ti-user" />
                <span>View Profile</span>
              </NavLink>

              <NavLink
                to="/Admin/EditProfile"
                className="gp-menu-item"
                onClick={() => setProfileOpen(false)}
                role="menuitem"
              >
                <i className="ti ti-edit" />
                <span>Edit Profile</span>
              </NavLink>

              <NavLink
                to="/Admin/Changepassword"
                className="gp-menu-item"
                onClick={() => setProfileOpen(false)}
                role="menuitem"
              >
                <i className="ti ti-lock" />
                <span>Change Password</span>
              </NavLink>

              <span className="gp-menu-sep" />

              <button
                type="button"
                className="gp-menu-item gp-logout"
                onClick={handleLogout}
                role="menuitem"
              >
                <i className="ti ti-logout" />
                <span>Sign out</span>
              </button>
            </div>
          </div>

          {/* Quick logout, kept outside the menu as before. */}
          <button
            type="button"
            className="gp-icon-btn gp-danger"
            title="Logout"
            aria-label="Logout"
            onClick={handleLogout}
          >
            <i className="ti ti-logout" />
          </button>
        </div>
      </div>
    </header>
  );
}
